/**
 * Router contract for `/create`.
 *
 * The composer is reachable from the home feed, a profile, the saved list and a
 * group, but it is a full page rather than an overlay, so it has to send the
 * user somewhere once the post is saved. `returnTo` carries the list the post
 * was opened from; without it every submit landed on the home feed, which threw
 * away the profile or saved-posts list the editor started on.
 */
import type { FeedPost } from '@wlb/shared/types/post';

/** Group carried in from a group screen; the post is locked to it. */
export interface CreatePostGroupTarget {
  id: string;
  name?: string | null;
  image?: string | null;
}

export interface CreatePostRouteState {
  editPost?: FeedPost;
  group?: CreatePostGroupTarget;
  /** Path the composer returns to after a successful submit. */
  returnTo?: string;
  /**
   * Window offset of the list the edit was started from. The composer is a page
   * rather than an overlay, so the list unmounts and comes back scrolled to the
   * top unless the offset travels with the edit.
   */
  returnScrollY?: number;
}

/** Router state the composer hands to the screen it returns to. */
export interface CreatePostReturnState {
  restoreScrollY?: number;
}

/**
 * Query parameter naming the post a list should reopen on arrival. Web has no
 * post-detail route — a single post is a full-view modal over its list — so the
 * post id rides in the query instead of the path.
 */
export const FOCUSED_POST_PARAM = 'post';

/** The path plus query of a router location, as a single `returnTo` value. */
export const createPostReturnPath = (from: {
  pathname: string;
  search?: string;
}): string => `${from.pathname}${from.search ?? ''}`;

/**
 * The given location with `?post=<id>` set, so returning to it reopens that one
 * post rather than only the list it sits in. Used when the composer is opened
 * from an already-open post viewer.
 */
export const focusedPostLocation = (
  from: { pathname: string; search?: string },
  postId: string,
): { pathname: string; search: string } => {
  const params = new URLSearchParams(from.search ?? '');
  params.set(FOCUSED_POST_PARAM, postId);
  return { pathname: from.pathname, search: `?${params.toString()}` };
};

/** A scroll offset worth restoring, or null for the top of the list. */
const usableOffset = (scrollY: number | null | undefined): number | null =>
  typeof scrollY === 'number' && Number.isFinite(scrollY) && scrollY > 0
    ? Math.round(scrollY)
    : null;

/** Router state for opening the composer on an existing post. */
export const editPostRouteState = (
  post: FeedPost,
  from: { pathname: string; search?: string },
  scrollY?: number | null,
): CreatePostRouteState => {
  const offset = usableOffset(scrollY);
  return {
    editPost: post,
    returnTo: createPostReturnPath(from),
    ...(offset === null ? {} : { returnScrollY: offset }),
  };
};

/**
 * Router state for the trip back. Null when there is nothing to restore, so a
 * post created from scratch still lands on the top of the feed with its new post.
 */
export const createPostReturnState = (
  scrollY: number | null | undefined,
): CreatePostReturnState | null => {
  const offset = usableOffset(scrollY);
  return offset === null ? null : { restoreScrollY: offset };
};

/** The offset a returning screen should restore; 0 when it arrived another way. */
export const readRestoreScrollY = (state: unknown): number =>
  usableOffset((state as CreatePostReturnState | null)?.restoreScrollY) ?? 0;

/**
 * Where a saved post lands. The screen that opened the composer wins, so an
 * edit started on a profile or the saved list returns to that list. A group
 * screen that opened the composer without a `returnTo` still falls back to its
 * own page, and anything else to the home feed.
 */
export const resolveCreatePostReturnPath = (
  returnTo: string | null | undefined,
  group: CreatePostGroupTarget | null,
): string => returnTo ?? (group ? `/groups/${group.id}` : '/');
